import { AutocompleteInput } from '@ipfabric/ui-components/AutocompleteInput/AutocompleteInput';
import type { SearchEvent } from '@ipfabric/ui-components/AutocompleteInput/AutocompleteInput.types';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import {
  type TableRequestBody,
  normalizeLabelDisplayToken,
} from '@ipfabric/utils';
import { useApiTableAutocomplete } from 'components/FormikTypeaheadInput/useApiTableAutocomplete';
import type { FC } from 'react';
import type { ApiRequestParams } from 'utils/request/ApiRequestParams';

type LabelSnapshotNameOption = {
  name: string;
};

type LabelSnapshotPairOption = {
  label: string;
};

type LabelsFilterConstructorProps = {
  mode: 'name' | 'exact';
  onBlur: () => void;
  onChange: (value: string) => void;
  value: string;
};

export const LabelsFilterConstructor: FC<LabelsFilterConstructorProps> = ({
  mode,
  onBlur,
  onChange,
  value,
}) => {
  const isNameMode = mode === 'name';

  const nameAutocomplete = useApiTableAutocomplete<LabelSnapshotNameOption>({
    debounced: 250,
    snapshot: true,
    fetchWithEmptyQuery: true,
    createRequest: (
      searchEvent,
    ): ApiRequestParams<TableRequestBody<LabelSnapshotNameOption>> => ({
      url: '/tables/label-snapshot-names',
      data: {
        columns: ['name'],
        filters: {
          name: ['like', searchEvent.inputValue],
        },
        sort: {
          column: 'name',
          order: 'asc',
        },
      },
    }),
  });

  const pairAutocomplete = useApiTableAutocomplete<LabelSnapshotPairOption>({
    debounced: 250,
    snapshot: true,
    fetchWithEmptyQuery: true,
    createRequest: (
      searchEvent,
    ): ApiRequestParams<TableRequestBody<LabelSnapshotPairOption>> => ({
      url: '/tables/label-snapshot-pairs',
      data: {
        columns: ['label'],
        filters: {
          label: ['like', normalizeLabelDisplayToken(searchEvent.inputValue)],
        },
        sort: {
          column: 'label',
          order: 'asc',
        },
      },
    }),
  });

  const options = isNameMode
    ? (nameAutocomplete.content?.map((row) => row.name) ?? null)
    : (pairAutocomplete.content?.map((row) => row.label) ?? null);

  const searchNames = useStableCallback(async (searchEvent: SearchEvent) => {
    const result = await nameAutocomplete.search(searchEvent);
    return {
      ...result,
      content: result.content?.map((row) => row.name) ?? null,
    };
  });

  const searchPairs = useStableCallback(async (searchEvent: SearchEvent) => {
    const result = await pairAutocomplete.search(searchEvent);
    return {
      ...result,
      content: result.content?.map((row) => row.label) ?? null,
    };
  });

  return (
    <AutocompleteInput.String
      loading={isNameMode ? nameAutocomplete.loading : pairAutocomplete.loading}
      onBlur={onBlur}
      onChange={onChange}
      onClear={() => onChange('')}
      onSearch={isNameMode ? searchNames : searchPairs}
      options={options}
      placeholder={
        isNameMode
          ? 'Select assigned label name'
          : 'Select exact assigned label'
      }
      value={value}
    />
  );
};
